import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SectionWrapper } from '../SectionWrapper';
import { Building2, MapPin, Plus, Trash2, FileText, Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useCompanyInfo } from '@/hooks/useCompanyInfo';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { autofillAddress, parseAddressManually } from '@/lib/addressAutofill';
import { useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

interface Location {
  id?: string;
  locationType: string;
  locationName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  squareFootage?: number;
  isSaved?: boolean;
}

interface StateComboboxProps {
  value: string;
  onValueChange: (value: string) => void;
}

const StateCombobox = ({ value, onValueChange }: StateComboboxProps) => {
  const [open, setOpen] = useState(false);
  const selectedState = US_STATES.find(state => state.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between mt-1"
        >
          {selectedState ? selectedState.label : "Select state..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput placeholder="Search state..." />
          <CommandList>
            <CommandEmpty>No state found.</CommandEmpty>
            <CommandGroup>
              {US_STATES.map((state) => (
                <CommandItem
                  key={state.value}
                  value={`${state.value} ${state.label}`}
                  onSelect={() => {
                    onValueChange(state.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === state.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {state.label} ({state.value})
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const BusinessProfileSection = () => {
  const navigate = useNavigate();
  const { completeSection, updateSectionData } = useOnboarding();
  const { data: companyData, isLoading: companyLoading } = useCompanyInfo();
  const queryClient = useQueryClient();
  const [conceptCount, setConceptCount] = useState(0); // Start with 0 to allow manual entry
  const [locations, setLocations] = useState<Location[]>([
    { locationType: '', locationName: '', address: '', city: '', state: '', zipCode: '', isSaved: false }
  ]);
  const [autofillLoading, setAutofillLoading] = useState<Record<number, boolean>>({});
  const autofillTimeouts = useRef<Record<number, NodeJS.Timeout>>({});
  const [expandedLocations, setExpandedLocations] = useState<string[]>([]); // Track which location accordions are open
  const [isLoadingData, setIsLoadingData] = useState(true); // Track loading state for locations
  const [businessInfo, setBusinessInfo] = useState({
    name: '', // Business Legal Name
    restaurant_name: '', // Restaurant Name
    entity_type: '',
    ein_number: '',
    sales_taxpayer_number: '', // Sales Tax ID #
    rep_name: '', // Rep Name
    rep_title: '', // Rep Title (Owner/Manager)
    email: '', // Contact Email
    phone: '', // Contact Cell Phone
  });
  const [isCompanySaved, setIsCompanySaved] = useState(false);
  const [isSavingCompany, setIsSavingCompany] = useState(false);

  const refreshCompanyInfo = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['company-info'] });
  }, [queryClient]);

  // Load existing data
  useEffect(() => {
    // If company data is still loading, wait
    if (companyLoading) {
      setIsLoadingData(true);
      return;
    }

    // If no company ID yet (new user), allow them to start filling the form
    if (!companyData?.id) {
      setIsLoadingData(false);
      setIsCompanySaved(false);
      // Ensure at least one location exists for new users
      if (locations.length === 0) {
        setLocations([{ locationType: '', locationName: '', address: '', city: '', state: '', zipCode: '', isSaved: false }]);
        setConceptCount(1);
      }
      return;
    }
    
    // Company exists, mark as saved
    setIsCompanySaved(true);

    setIsLoadingData(true);

    const loadData = async () => {
      try {
        // First, try to load from onboarding_progress data_snapshot (for unsaved form data)
        const { data: progressData } = await supabase
          .from('onboarding_progress')
          .select('data_snapshot')
          .eq('company_id', companyData.id)
          .single();

        const savedData = (progressData?.data_snapshot as any)?.business_profile;
        
        // Load company info - prioritize saved form data over database
        if (companyData) {
          const savedBusinessInfo = savedData?.businessInfo;
          setBusinessInfo({
            name: savedBusinessInfo?.name || companyData.name || '',
            restaurant_name: savedBusinessInfo?.restaurant_name || companyData.restaurant_name || '',
            entity_type: savedBusinessInfo?.entity_type || companyData.entity_type || '',
            ein_number: savedBusinessInfo?.ein_number || companyData.ein_number || '',
            sales_taxpayer_number: savedBusinessInfo?.sales_taxpayer_number || companyData.sales_taxpayer_number || '',
            rep_name: savedBusinessInfo?.rep_name || (companyData as any).rep_name || '',
            rep_title: savedBusinessInfo?.rep_title || (companyData as any).rep_title || '',
            email: savedBusinessInfo?.email || companyData.email || '',
            phone: savedBusinessInfo?.phone || companyData.phone || '',
          });
        }

        // Load locations - prioritize saved form data
        let loadedLocations: Location[] = [];
        
        if (savedData?.locations && Array.isArray(savedData.locations) && savedData.locations.length > 0) {
          // Use saved form data (may include unsaved locations)
          loadedLocations = savedData.locations.map((loc: any) => ({
            id: loc.id,
            locationType: loc.locationType || loc.concept_name || '',
            locationName: loc.locationName || loc.location_name || '',
            address: loc.address || '',
            city: loc.city || '',
            state: loc.state || '',
            zipCode: loc.zipCode || loc.zip_code || '',
            squareFootage: loc.squareFootage || loc.square_footage || undefined,
            isSaved: !!loc.id, // Only saved if it has an ID
          }));
        } else {
          // Fall back to database
          const { data: locationsData, error: locError } = await supabase
            .from('locations')
            .select('*')
            .eq('company_id', companyData.id);

          if (!locError && locationsData && locationsData.length > 0) {
            loadedLocations = locationsData.map(loc => ({
              id: loc.id,
              locationType: loc.concept_name || '',
              locationName: loc.location_name,
              address: loc.address || '',
              city: loc.city || '',
              state: loc.state || '',
              zipCode: loc.zip_code || '',
              squareFootage: loc.square_footage || undefined,
              isSaved: true,
            }));
          }
        }

        if (loadedLocations.length > 0) {
          setLocations(loadedLocations);
          setConceptCount(savedData?.conceptCount || loadedLocations.length);
        } else {
          // Ensure at least one location exists
          if (locations.length === 0) {
            setLocations([{ locationType: '', locationName: '', address: '', city: '', state: '', zipCode: '', isSaved: false }]);
            setConceptCount(1);
          }
        }

      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, [companyData?.id, companyLoading]);

  const addLocation = () => {
    setLocations([...locations, { locationType: '', locationName: '', address: '', city: '', state: '', zipCode: '', isSaved: false }]);
    setConceptCount(conceptCount + 1);
  };

  const removeLocation = (index: number) => {
    if (locations.length > 1) {
      setLocations(locations.filter((_, i) => i !== index));
      setConceptCount(conceptCount - 1);
    }
  };

  const updateLocation = (index: number, field: keyof Location, value: string | number) => {
    const updated = [...locations];
    updated[index] = { ...updated[index], [field]: value, isSaved: false };
    setLocations(updated);
  };

  // Debounced address autofill function
  const handleAddressChange = useCallback((index: number, address: string) => {
    // Update the address field immediately
    updateLocation(index, 'address', address);

    // Clear existing timeout for this location
    if (autofillTimeouts.current[index]) {
      clearTimeout(autofillTimeouts.current[index]);
    }

    // Only autofill if address is at least 10 characters
    if (address.length < 10) {
      return;
    }

    // Set loading state
    setAutofillLoading(prev => ({ ...prev, [index]: true }));

    // Debounce the autofill call
    autofillTimeouts.current[index] = setTimeout(async () => {
      try {
        // Try Google Places API first
        const result = await autofillAddress(address);
        
        if (result.success && result.data) {
          // Update city, state, and zip
          const updated = [...locations];
          updated[index] = {
            ...updated[index],
            city: result.data.city || updated[index].city,
            state: result.data.state || updated[index].state,
            zipCode: result.data.zip || updated[index].zipCode,
            isSaved: false,
          };
          setLocations(updated);
          toast.success('Address details autofilled');
        } else {
          // Fallback to manual parsing
          const parsed = parseAddressManually(address);
          if (parsed) {
            const updated = [...locations];
            updated[index] = {
              ...updated[index],
              city: parsed.city || updated[index].city,
              state: parsed.state || updated[index].state,
              zipCode: parsed.zip || updated[index].zipCode,
              isSaved: false,
            };
            setLocations(updated);
          }
        }
      } catch (error) {
        console.error('Error in address autofill:', error);
        // Silently fail - user can still enter manually
      } finally {
        setAutofillLoading(prev => ({ ...prev, [index]: false }));
      }
    }, 800); // 800ms debounce
  }, [locations]);

  const saveLocation = async (index: number) => {
    const location = locations[index];
    
    // Validate required fields
    if (!location.locationType || !location.locationName || !location.address || !location.city || !location.state || !location.zipCode) {
      toast.error('Please fill in all required fields for this location');
      return;
    }

    let companyId = companyData?.id;
    
    // If company doesn't exist yet, we need to create it first
    if (!companyId) {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        toast.error('User not authenticated');
        return;
      }

      // Create company with minimal info
      if (!businessInfo.name || !businessInfo.restaurant_name || !businessInfo.entity_type) {
        toast.error('Please complete business information (Business Legal Name, Restaurant Name, and Entity Type) first');
        return;
      }

      const { data: newCompany, error: createError } = await supabase
        .from('companies')
        .insert({
          name: businessInfo.name,
          restaurant_name: businessInfo.restaurant_name,
          entity_type: businessInfo.entity_type as any,
          created_by: currentUser.id, // Required by schema, trigger will also set it
        } as any) // Type assertion to handle new fields that may not be in types yet
        .select()
        .single();

      if (createError || !newCompany) {
        toast.error('Failed to create company. Please complete business information first.');
        return;
      }

      companyId = newCompany.id;

      // Update user's profile with company_id
      await supabase
        .from('profiles')
        .update({ company_id: companyId })
        .eq('user_id', currentUser.id);

      // Ensure company info query picks up the new company id for onboarding progress
      await refreshCompanyInfo();

      // Mark company as saved so we don't try to create it again
      setIsCompanySaved(true);
      
      // Update business info state with the new company data
      setBusinessInfo(prev => ({
        ...prev,
        name: newCompany.name || prev.name,
        restaurant_name: newCompany.restaurant_name || prev.restaurant_name,
        entity_type: newCompany.entity_type || prev.entity_type,
      }));

      // Wait a moment for the profile update to propagate through RLS policies
      // This ensures the location insert won't fail due to RLS checking stale profile data
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    try {
      if (location.id) {
        // Update existing
        const { error, data } = await supabase
          .from('locations')
          .update({
            concept_name: location.locationType,
            location_name: location.locationName,
            address: location.address,
            city: location.city,
            state: location.state,
            zip_code: location.zipCode,
            square_footage: location.squareFootage || null,
          })
          .eq('id', location.id)
          .select()
          .single();

        if (error) throw error;
        
        const updated = [...locations];
        updated[index] = { 
          ...location, 
          id: data.id,
          isSaved: true 
        };
        setLocations(updated);
        toast.success('Location saved successfully');
        
        // Collapse the accordion after saving
        setExpandedLocations(prev => prev.filter(item => item !== `location-${index}`));
      } else {
        // Insert new
        const { error, data } = await supabase
          .from('locations')
          .insert({
            company_id: companyId,
            concept_name: location.locationType,
            location_name: location.locationName,
            address: location.address,
            city: location.city,
            state: location.state,
            zip_code: location.zipCode,
            square_footage: location.squareFootage || null,
          })
          .select()
          .single();

        if (error) throw error;
        
        const updated = [...locations];
        updated[index] = { 
          ...location, 
          id: data.id,
          isSaved: true 
        };
        setLocations(updated);
        toast.success('Location saved successfully');
        
        // Collapse the accordion after saving
        setExpandedLocations(prev => prev.filter(item => item !== `location-${index}`));
      }
    } catch (error: any) {
      console.error('Error saving location:', error);
      toast.error(error.message || 'Failed to save location');
    }
  };

  // Check if business info and locations are valid
  const isBusinessInfoValid = businessInfo.name && 
    businessInfo.restaurant_name && 
    businessInfo.entity_type && 
    businessInfo.rep_name && 
    businessInfo.rep_title && 
    businessInfo.email && 
    businessInfo.phone;
  // At least one location must be complete, but not all locations need to be complete
  const hasValidLocation = locations.length > 0 && locations.some(loc => 
    loc.locationType && loc.locationName && loc.address && loc.city && loc.state && loc.zipCode
  );
  const canProceed = isCompanySaved && isBusinessInfoValid && hasValidLocation;

  const handleNext = async () => {
    // Validate business info
    if (!businessInfo.name || !businessInfo.restaurant_name || !businessInfo.entity_type || 
        !businessInfo.rep_name || !businessInfo.rep_title || !businessInfo.email || !businessInfo.phone) {
      toast.error('Please fill in all required business information fields');
      return;
    }

    // Validate locations - require at least one complete location
    if (locations.length === 0) {
      toast.error('Please add at least one location');
      return;
    }

    const completeLocations = locations.filter(loc => 
      loc.locationType && loc.locationName && loc.address && loc.city && loc.state && loc.zipCode
    );

    if (completeLocations.length === 0) {
      toast.error('Please fill in all required fields for at least one location');
      return;
    }

    // Warn if there are incomplete locations, but still allow proceeding
    const incompleteLocations = locations.filter(loc => 
      !loc.locationType || !loc.locationName || !loc.address || !loc.city || !loc.state || !loc.zipCode
    );

    if (incompleteLocations.length > 0) {
      toast.warning(`Saving ${completeLocations.length} complete location(s). ${incompleteLocations.length} incomplete location(s) will not be saved.`);
    }

    let currentCompanyId = companyData?.id;

    // Create company if it doesn't exist
    if (!currentCompanyId) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('User not authenticated');
        return;
      }

      // Create new company
      const { data: newCompany, error: createError } = await supabase
        .from('companies')
        .insert({
          name: businessInfo.name,
          restaurant_name: businessInfo.restaurant_name,
          entity_type: businessInfo.entity_type as any,
          ein_number: businessInfo.ein_number || null,
          sales_taxpayer_number: businessInfo.sales_taxpayer_number || null,
          rep_name: businessInfo.rep_name,
          rep_title: businessInfo.rep_title,
          email: businessInfo.email,
          phone: businessInfo.phone,
          location_count: completeLocations.length,
          has_multiple_locations: completeLocations.length > 1,
          is_multi_concept: new Set(completeLocations.map(l => l.locationType)).size > 1,
          created_by: user.id, // user is already defined above
        } as any) // Type assertion for new fields not yet in generated types
        .select()
        .single();

      if (createError || !newCompany) {
        toast.error('Failed to create company');
        console.error(createError);
        return;
      }

      currentCompanyId = newCompany.id;

      // Update user's profile with company_id
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ company_id: currentCompanyId })
        .eq('user_id', user.id);

      if (profileError) {
        toast.error('Failed to link company to profile');
        console.error(profileError);
        return;
      }

      // Refresh company info so onboarding progress can be saved immediately
      await refreshCompanyInfo();

      // Mark company as saved to prevent duplicate creation
      setIsCompanySaved(true);

      // Wait a moment for the profile update to propagate through RLS policies
      // This ensures the locations insert won't fail due to RLS checking stale profile data
      await new Promise(resolve => setTimeout(resolve, 500));

      // Assign company_admin role to the user - get fresh user ID to ensure correctness
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        const { error: roleError } = await supabase
          .from('user_roles')
          .upsert({ 
            user_id: currentUser.id, 
            role: 'company_admin' 
          }, {
            onConflict: 'user_id,role'
          });

        if (roleError) {
          console.error('Failed to assign role:', roleError);
          // Don't block onboarding if role assignment fails
        }
      }
    } else {
      // Update existing company info
      const { error: companyError } = await supabase
        .from('companies')
        .update({
          name: businessInfo.name,
          restaurant_name: businessInfo.restaurant_name,
          entity_type: businessInfo.entity_type as any,
          ein_number: businessInfo.ein_number || null,
          sales_taxpayer_number: businessInfo.sales_taxpayer_number || null,
          rep_name: businessInfo.rep_name,
          rep_title: businessInfo.rep_title,
          email: businessInfo.email,
          phone: businessInfo.phone,
          location_count: completeLocations.length,
          has_multiple_locations: completeLocations.length > 1,
          is_multi_concept: new Set(completeLocations.map(l => l.locationType)).size > 1,
        } as any) // Type assertion for new fields not yet in generated types
        .eq('id', currentCompanyId);

      if (companyError) {
        toast.error('Failed to update business information');
        console.error(companyError);
        return;
      }

      // Ensure company_admin role is assigned when updating existing company
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        const { error: roleError } = await supabase
          .from('user_roles')
          .upsert({ 
            user_id: currentUser.id, 
            role: 'company_admin' 
          }, {
            onConflict: 'user_id,role'
          });

        if (roleError) {
          console.error('Failed to assign role:', roleError);
          // Don't block onboarding if role assignment fails
        }
      }
    }

    // Save locations - only save complete locations, prevent duplicates
    const updatedLocations = [];
    for (const location of locations) {
      // Skip incomplete locations (only save complete ones)
      if (!location.locationType || !location.locationName || !location.address || 
          !location.city || !location.state || !location.zipCode) {
        continue;
      }
      if (location.id) {
        // Update existing
        const { error } = await supabase
          .from('locations')
          .update({
            concept_name: location.locationType,
            location_name: location.locationName,
            address: location.address,
            city: location.city,
            state: location.state,
            zip_code: location.zipCode,
            square_footage: location.squareFootage || null,
          })
          .eq('id', location.id);

        if (error) {
          toast.error('Failed to update location');
          console.error(error);
          return;
        }
        updatedLocations.push(location);
      } else {
        // Check if location with same name already exists for this company
        const { data: existingLocation } = await supabase
          .from('locations')
          .select('id')
          .eq('company_id', currentCompanyId)
          .eq('location_name', location.locationName)
          .maybeSingle();

        if (existingLocation) {
          // Update existing instead of inserting
          const { error } = await supabase
            .from('locations')
            .update({
              concept_name: location.locationType,
              location_name: location.locationName,
              address: location.address,
              city: location.city,
              state: location.state,
              zip_code: location.zipCode,
              square_footage: location.squareFootage || null,
            })
            .eq('id', existingLocation.id);

          if (error) {
            toast.error('Failed to update location');
            console.error(error);
            return;
          }
          updatedLocations.push({ ...location, id: existingLocation.id });
        } else {
          // Insert new
          const { data: insertedLocation, error } = await supabase
            .from('locations')
            .insert({
              company_id: currentCompanyId,
              concept_name: location.locationType,
              location_name: location.locationName,
              address: location.address,
              city: location.city,
              state: location.state,
              zip_code: location.zipCode,
              square_footage: location.squareFootage || null,
            })
            .select()
            .single();

          if (error) {
            toast.error('Failed to save location');
            console.error(error);
            return;
          }
          updatedLocations.push({ ...location, id: insertedLocation.id });
        }
      }
    }
    // Update local state with IDs to prevent future duplicates
    setLocations(updatedLocations);

    await completeSection('business_profile', { 
      businessInfo, 
      locations: updatedLocations, // Only save complete locations
      conceptCount: updatedLocations.length, // Use count of saved locations
      hasMultipleLocations: updatedLocations.length > 1,
      isMultiConcept: new Set(updatedLocations.map(l => l.locationType)).size > 1,
    });
    
    toast.success('Business profile saved successfully!');
    
    // Navigate to the next section (Bank Account Setup & Training)
    setTimeout(() => {
      navigate('/onboarding/bank-account-training');
    }, 500);
  };

  const handleSaveCompanyInfo = async () => {
    // Validate required fields
    if (!businessInfo.name || !businessInfo.restaurant_name || !businessInfo.entity_type ||
        !businessInfo.rep_name || !businessInfo.rep_title || !businessInfo.email || !businessInfo.phone) {
      toast.error('Please fill in all required business information fields');
      return;
    }

    setIsSavingCompany(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('User not authenticated');
        return;
      }

      let currentCompanyId = companyData?.id;

      // Create or update company
      if (!currentCompanyId) {
        const { data: newCompany, error: createError } = await supabase
          .from('companies')
          .insert({
            name: businessInfo.name,
            restaurant_name: businessInfo.restaurant_name,
            entity_type: businessInfo.entity_type as any,
            ein_number: businessInfo.ein_number || null,
            sales_taxpayer_number: businessInfo.sales_taxpayer_number || null,
            rep_name: businessInfo.rep_name,
            rep_title: businessInfo.rep_title,
            email: businessInfo.email,
            phone: businessInfo.phone,
            created_by: user.id,
          } as any)
          .select()
          .single();

        if (createError || !newCompany) {
          toast.error('Failed to create company');
          console.error('Create company error:', createError);
          return;
        }

        currentCompanyId = newCompany.id;

        const { error: profileError } = await supabase
          .from('profiles')
          .update({ company_id: currentCompanyId })
          .eq('user_id', user.id);

        if (profileError) {
          console.error('Failed to link company to profile:', profileError);
        }

        // Assign company_admin role to the user
        const { error: roleError } = await supabase
          .from('user_roles')
          .upsert({ 
            user_id: user.id, 
            role: 'company_admin' 
          }, {
            onConflict: 'user_id,role'
          });

        if (roleError) {
          console.error('Failed to assign role:', roleError);
        }

        // Refresh cached company info
        await refreshCompanyInfo();

        // Mark company as saved so we don't create duplicates
        setIsCompanySaved(true);

        // Wait for RLS policies to propagate before allowing location saves
        await new Promise(resolve => setTimeout(resolve, 500));

        toast.success('Company information saved successfully! You can now add locations.');
      } else {
        const { error: companyError } = await supabase
          .from('companies')
          .update({
            name: businessInfo.name,
            restaurant_name: businessInfo.restaurant_name,
            entity_type: businessInfo.entity_type as any,
            ein_number: businessInfo.ein_number || null,
            sales_taxpayer_number: businessInfo.sales_taxpayer_number || null,
            rep_name: businessInfo.rep_name,
            rep_title: businessInfo.rep_title,
            email: businessInfo.email,
            phone: businessInfo.phone,
          } as any)
          .eq('id', currentCompanyId);

        if (companyError) {
          toast.error('Failed to update company information');
          console.error('Update company error:', companyError);
          return;
        }

        toast.success('Company information updated successfully!');
      }
    } finally {
      setIsSavingCompany(false);
    }
  };

  const handleSave = async () => {
    // Save without advancing - just update section data
    if (!businessInfo.name || !businessInfo.restaurant_name || !businessInfo.entity_type) {
      toast.error('Please fill in required business information (Business Legal Name, Restaurant Name, and Entity Type)');
      return;
    }

    let currentCompanyId = companyData?.id;

    // Create or update company
    if (!currentCompanyId) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('User not authenticated');
        return;
      }

      const { data: newCompany, error: createError } = await supabase
        .from('companies')
        .insert({
          name: businessInfo.name,
          restaurant_name: businessInfo.restaurant_name,
          entity_type: businessInfo.entity_type as any,
          ein_number: businessInfo.ein_number || null,
          sales_taxpayer_number: businessInfo.sales_taxpayer_number || null,
          rep_name: businessInfo.rep_name,
          rep_title: businessInfo.rep_title,
          email: businessInfo.email,
          phone: businessInfo.phone,
          created_by: user.id,
        } as any)
        .select()
        .single();

      if (createError || !newCompany) {
        toast.error('Failed to save business information');
        console.error('Create company error:', createError);
        return;
      }

      currentCompanyId = newCompany.id;

      const { error: profileError } = await supabase
        .from('profiles')
        .update({ company_id: currentCompanyId })
        .eq('user_id', user.id);

      if (profileError) {
        console.error('Failed to link company to profile:', profileError);
        // Don't block save if profile update fails
      }

      // Refresh cached company info so onboarding progress can persist
      await refreshCompanyInfo();

      // Mark company as saved so we don't create duplicates
      setIsCompanySaved(true);

      // Wait for RLS policies to propagate
      await new Promise(resolve => setTimeout(resolve, 500));
    } else {
      const { error: companyError } = await supabase
        .from('companies')
        .update({
          name: businessInfo.name,
          restaurant_name: businessInfo.restaurant_name,
          entity_type: businessInfo.entity_type as any,
          ein_number: businessInfo.ein_number || null,
          sales_taxpayer_number: businessInfo.sales_taxpayer_number || null,
          rep_name: businessInfo.rep_name,
          rep_title: businessInfo.rep_title,
          email: businessInfo.email,
          phone: businessInfo.phone,
        } as any)
        .eq('id', currentCompanyId);

      if (companyError) {
        toast.error('Failed to save business information');
        console.error('Update company error:', companyError);
        return;
      }
    }

    // Save locations - only save complete locations
    const completeLocations = locations.filter(loc => 
      loc.locationType && loc.locationName && loc.address && loc.city && loc.state && loc.zipCode
    );

    const updatedLocations = [];
    for (const location of completeLocations) {
      if (location.id) {
        const { error: updateError } = await supabase
          .from('locations')
          .update({
            concept_name: location.locationType,
            location_name: location.locationName,
            address: location.address,
            city: location.city,
            state: location.state,
            zip_code: location.zipCode,
            square_footage: location.squareFootage || null,
          })
          .eq('id', location.id);
        
        if (updateError) {
          console.error('Error updating location:', updateError);
          toast.error(`Failed to update location: ${location.locationName}`);
          return;
        }
        updatedLocations.push(location);
      } else {
        // Check if location with same name already exists
        const { data: existingLocation } = await supabase
          .from('locations')
          .select('id')
          .eq('company_id', currentCompanyId)
          .eq('location_name', location.locationName)
          .maybeSingle();

        if (existingLocation) {
          // Update existing
          const { error: updateError } = await supabase
            .from('locations')
            .update({
              concept_name: location.locationType,
              location_name: location.locationName,
              address: location.address,
              city: location.city,
              state: location.state,
              zip_code: location.zipCode,
              square_footage: location.squareFootage || null,
            })
            .eq('id', existingLocation.id);
          
          if (updateError) {
            console.error('Error updating location:', updateError);
            toast.error(`Failed to update location: ${location.locationName}`);
            return;
          }
          updatedLocations.push({ ...location, id: existingLocation.id });
        } else {
          // Insert new
          const { data: insertedLocation, error: insertError } = await supabase
            .from('locations')
            .insert({
              company_id: currentCompanyId,
              concept_name: location.locationType,
              location_name: location.locationName,
              address: location.address,
              city: location.city,
              state: location.state,
              zip_code: location.zipCode,
              square_footage: location.squareFootage || null,
            })
            .select()
            .single();
          
          if (insertError) {
            console.error('Error inserting location:', insertError);
            toast.error(`Failed to save location: ${location.locationName}`);
            return;
          }
          updatedLocations.push({ ...location, id: insertedLocation.id });
        }
      }
    }

    // Update local state with saved locations
    setLocations(updatedLocations);

    // Update section data and mark as in_progress (not completed, since user clicked Save, not Complete)
    await updateSectionData('business_profile', {
      businessInfo,
      locations: updatedLocations,
      conceptCount: updatedLocations.length,
      hasMultipleLocations: updatedLocations.length > 1,
      isMultiConcept: new Set(updatedLocations.map(l => l.locationType)).size > 1,
    });

    toast.success('Business information saved successfully');
  };

  // Show loading state while data is being fetched
  if (companyLoading || isLoadingData) {
    return (
      <SectionWrapper
        title="Business Profile & Tax Information"
        description="Complete business details, tax information, locations, and ownership structure. Shareholders/Partners are optional."
        icon={<Building2 className="h-6 w-6" />}
        onNext={handleNext}
        showSaveButton={false}
        nextLabel="Save & Continue"
        isNextDisabled={!canProceed}
      >
        <div className="flex flex-col items-center justify-center py-16 space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <div className="text-center space-y-2">
            <p className="text-lg font-medium">Loading your business information...</p>
            <p className="text-sm text-muted-foreground">
              Please wait while we fetch your company details and locations.
            </p>
          </div>
        </div>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Business Profile & Tax Information"
      description="Complete business details, tax information, and locations."
      icon={<Building2 className="h-6 w-6" />}
      onNext={handleNext}
      showSaveButton={false}
      nextLabel="Save & Continue"
      isNextDisabled={!canProceed}
    >
      <div className="space-y-6">
        {/* Business Information Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Business Details
            </CardTitle>
          </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Business Legal Name *</Label>
                  <Input
                    value={businessInfo.name}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, name: e.target.value })}
                    placeholder="Enter business legal name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Restaurant Name *</Label>
                  <Input
                    value={businessInfo.restaurant_name}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, restaurant_name: e.target.value })}
                    placeholder="Enter restaurant name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Entity Type *</Label>
                  <Select
                    value={businessInfo.entity_type}
                    onValueChange={(value) => setBusinessInfo({ ...businessInfo, entity_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select entity type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sole_proprietorship">Sole Proprietorship</SelectItem>
                      <SelectItem value="partnership">Partnership</SelectItem>
                      <SelectItem value="s_corporation">S Corporation</SelectItem>
                      <SelectItem value="c_corporation">C Corporation</SelectItem>
                      <SelectItem value="llc">LLC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>EIN #</Label>
                  <Input
                    value={businessInfo.ein_number}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, ein_number: e.target.value })}
                    placeholder="XX-XXXXXXX"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Sales Tax ID #</Label>
                  <Input
                    value={businessInfo.sales_taxpayer_number}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, sales_taxpayer_number: e.target.value })}
                    placeholder="Enter sales tax ID"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Rep Name *</Label>
                  <Input
                    value={businessInfo.rep_name}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, rep_name: e.target.value })}
                    placeholder="Enter representative name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Rep Title (Owner/Manager) *</Label>
                  <Select
                    value={businessInfo.rep_title}
                    onValueChange={(value) => setBusinessInfo({ ...businessInfo, rep_title: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select title" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Owner">Owner</SelectItem>
                      <SelectItem value="Manager">Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Contact Email *</Label>
                  <Input
                    type="email"
                    value={businessInfo.email}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, email: e.target.value })}
                    placeholder="contact@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Contact Cell Phone *</Label>
                  <Input
                    type="tel"
                    value={businessInfo.phone}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

        {/* Save Company Info Button */}
        {!isCompanySaved && (
          <div className="flex flex-col items-center gap-3 p-6 bg-primary/5 border-2 border-primary/20 rounded-lg">
            <p className="text-sm text-muted-foreground text-center">
              Save your company information to unlock the Locations section
            </p>
            <Button 
              onClick={handleSaveCompanyInfo} 
              disabled={
                !businessInfo.name || 
                !businessInfo.restaurant_name || 
                !businessInfo.entity_type || 
                !businessInfo.rep_name || 
                !businessInfo.rep_title || 
                !businessInfo.email || 
                !businessInfo.phone ||
                isSavingCompany
              }
              className="w-full max-w-md"
            >
              {isSavingCompany ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Save Company Info
                </>
              )}
            </Button>
          </div>
        )}

        {/* Locations Section */}
        {!isCompanySaved && (
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
            <p className="text-sm text-amber-900">
              <strong>⚠️ Locked:</strong> Please save your company information first to enable the Locations section.
            </p>
          </div>
        )}
        <Card className={cn(!isCompanySaved && "opacity-50 pointer-events-none")}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Locations
            </CardTitle>
          </CardHeader>
            <CardContent className="space-y-6">
              {/* Location Count */}
              <div>
                <Label>How many locations do you operate?</Label>
                <Input
                  type="number"
                  min={1}
                  value={conceptCount || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty value for manual entry
                    if (value === '') {
                      setConceptCount(0);
                      return;
                    }
                    const count = parseInt(value);
                    if (!isNaN(count) && count >= 1) {
                      setConceptCount(count);
                      
                      if (count > locations.length) {
                        // Add more locations
                        const newLocations = [...locations];
                        for (let i = locations.length; i < count; i++) {
                          newLocations.push({ locationType: '', locationName: '', address: '', city: '', state: '', zipCode: '', isSaved: false });
                        }
                        setLocations(newLocations);
                      } else if (count < locations.length) {
                        // Remove excess locations
                        setLocations(locations.slice(0, count));
                      }
                    }
                  }}
                  onBlur={(e) => {
                    // Ensure minimum of 1 on blur if empty
                    if (!e.target.value || parseInt(e.target.value) < 1) {
                      setConceptCount(1);
                      if (locations.length === 0) {
                        setLocations([{ locationType: '', locationName: '', address: '', city: '', state: '', zipCode: '', isSaved: false }]);
                      }
                    }
                  }}
                  placeholder="Enter number of locations"
                  className="max-w-xs mt-2"
                />
              </div>

              {/* Locations List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base">Location Details</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLocation}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Location
                  </Button>
                </div>

                <Accordion 
                  type="multiple" 
                  className="space-y-2"
                  value={expandedLocations}
                  onValueChange={setExpandedLocations}
                >
                  {locations.map((location, index) => (
                    <AccordionItem key={index} value={`location-${index}`}>
                      <Card className="border-2">
                        <AccordionTrigger className="px-4 py-3 hover:no-underline">
                          <div className="flex items-center gap-3 flex-1">
                            <MapPin className="h-5 w-5 text-muted-foreground" />
                            <div className="flex-1 text-left">
                              <div className="font-medium">
                                {location.locationName || `Location ${index + 1}`}
                                {location.locationType && (
                                  <span className="text-muted-foreground">
                                    {' '}({location.locationType === 'full_service_restaurant' ? 'Full Service Restaurant' :
                                      location.locationType === 'quick_service_restaurant' ? 'Quick Service Restaurant' :
                                      location.locationType === 'food_hall' ? 'Food Hall' :
                                      location.locationType === 'food_truck_trailer' ? 'Food Truck/Trailer' :
                                      location.locationType === 'popup' ? 'Popup' : location.locationType})
                                  </span>
                                )}
                              </div>
                              {location.address && (
                                <div className="text-sm text-muted-foreground">
                                  {location.address}, {location.city}, {US_STATES.find(s => s.value === location.state)?.label || location.state}
                                </div>
                              )}
                            </div>
                            {locations.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeLocation(index);
                                }}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                            <div>
                              <Label>Location Type *</Label>
                              <Select
                                value={location.locationType}
                                onValueChange={(value) => updateLocation(index, 'locationType', value)}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="Select location type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="full_service_restaurant">Full Service Restaurant</SelectItem>
                                  <SelectItem value="quick_service_restaurant">Quick Service Restaurant</SelectItem>
                                  <SelectItem value="food_hall">Food Hall</SelectItem>
                                  <SelectItem value="food_truck_trailer">Food Truck/Trailer</SelectItem>
                                  <SelectItem value="popup">Popup</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <Label>Location Name *</Label>
                              <Input
                                value={location.locationName}
                                onChange={(e) => updateLocation(index, 'locationName', e.target.value)}
                                placeholder="e.g., Downtown"
                                className="mt-1"
                              />
                            </div>

                            <div className="md:col-span-2">
                              <Label>Address *</Label>
                              <div className="relative mt-1">
                                <Input
                                  value={location.address}
                                  onChange={(e) => handleAddressChange(index, e.target.value)}
                                  placeholder="123 Main Street"
                                  className="pr-10"
                                />
                                {autofillLoading[index] && (
                                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                  </div>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Start typing an address to autofill city, state, and ZIP
                              </p>
                            </div>

                            <div>
                              <Label>City *</Label>
                              <Input
                                value={location.city}
                                onChange={(e) => updateLocation(index, 'city', e.target.value)}
                                placeholder="New York"
                                className="mt-1"
                              />
                            </div>

                            <div>
                              <Label>State *</Label>
                              <StateCombobox
                                value={location.state}
                                onValueChange={(value) => updateLocation(index, 'state', value)}
                              />
                            </div>

                            <div>
                              <Label>ZIP Code *</Label>
                              <Input
                                value={location.zipCode}
                                onChange={(e) => updateLocation(index, 'zipCode', e.target.value)}
                                placeholder="10001"
                                className="mt-1"
                              />
                            </div>

                            <div>
                              <Label>Square Footage (Optional)</Label>
                              <Input
                                type="number"
                                value={location.squareFootage || ''}
                                onChange={(e) => updateLocation(index, 'squareFootage', parseInt(e.target.value) || 0)}
                                placeholder="2000"
                                className="mt-1"
                              />
                            </div>
                          </div>
                        </AccordionContent>
                      </Card>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </CardContent>
          </Card>

      </div>
    </SectionWrapper>
  );
};
